import { FC, useEffect, useMemo } from "react";

import { PageLink } from "./PageLink";
import { useFetchAndSelectMyAssessments } from "./assessment-data";

import { getPageType, tableDateFormat } from "components/materials/page/helpers";
import { displayOutcomeName } from "components/outcomes/helpers";
import { ActivityType } from "components/server-types";
import { ScrollableTableContainer } from "components/tables/ScrollableTableContainer";
import { TD, TH, THead, TR, Table } from "components/tables/TableElements";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Tooltip } from "mds/components/Tooltip";
import { AssessPointsMarkerIcon, CommentIcon, ScoreIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  selectActivities,
  selectCurrentCourseId,
  selectCurrentUserId,
  selectFullCourseOutcomes,
  selectPageOutcomes,
  selectPagesWithAssessmentsForUserId,
  selectUserById,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { averageAndRound } from "utils/math";

type CourseAssessmentStudentViewProps = {
  userId: string | null;
};

export const CourseAssessmentStudentView: FC<CourseAssessmentStudentViewProps> = ({ userId }) => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const courseId = useAppSelector(selectCurrentCourseId);
  const courseOutcomes = useAppSelector((s) => selectFullCourseOutcomes(s, courseId));
  // TODO: Should we bundle these in the selectors somehow?
  const pageOutcomes = useAppSelector(selectPageOutcomes);
  const activities = useListSelector(selectActivities);
  const studentUser = useAppSelector((s) => selectUserById(s, userId));
  const isViewingOwnAssessments = currentUserId === userId;

  // This includes page groups and submissions
  useFetchAndSelectMyAssessments();

  useEffect(() => {
    if (courseId) {
      storeApi.page_outcomes.list({ course_id: courseId });
    }
  }, [courseId]);

  const activitiesById = useMemo(
    () =>
      activities.reduce(
        (acc, activity) => ({
          ...acc,
          [activity.id]: activity,
        }),
        {} as Record<string, ActivityType>,
      ),
    [activities],
  );

  const pagesWithAssessments = useAppSelector((s) =>
    selectPagesWithAssessmentsForUserId(s, userId, courseId),
  );
  const allAssessments = pagesWithAssessments.map((p) => p.assessments).flat();
  const allOutcomeAssessments = allAssessments.map((a) => a.outcome_assessments || []).flat();

  if (pagesWithAssessments.length === 0) {
    return (
      <div className="mb-4 flex flex-col gap-4 rounded-lg bg-black-tint-97 p-4">
        <IconText
          iconStart={<AssessPointsMarkerIcon className="text-blue" />}
          text={t(
            isViewingOwnAssessments
              ? "assessment_dashboard.all.empty_title"
              : "assessment_dashboard.all.empty_title_instructor",
          )}
        />
        <div className="body-s">
          {t(
            isViewingOwnAssessments
              ? "assessment_dashboard.all.empty_description"
              : "assessment_dashboard.all.empty_description_instructor",
            {
              studentName: studentUser?.first_name || t("assessment_dashboard.all.unknown_student"),
            },
          )}
        </div>
      </div>
    );
  }

  return (
    <main className="assessment-dashboard overflow-hidden">
      <ScrollableTableContainer
        className="w-full overflow-hidden"
        title={t("assessment_dashboard.all.title")}
      >
        <Table>
          {/* Headers: Page / Page Type / Points / Outcomes / Submitted At */}
          <THead>
            <TR className="border-black-tint-70">
              <TH className="pl-4">{t("assessment_dashboard.all.page_topic_header_title")}</TH>

              <TH>{t("assessment_dashboard.all.page_header_title")}</TH>

              <TH>
                <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                  {t("assessment_dashboard.all.page_type_header_title")}
                </Tooltip>
              </TH>

              <TH>{t("assessment_dashboard.student.points_header_title")}</TH>

              <TH>{t("assessment_dashboard.all.outcomes_header_title")}</TH>

              <TH>{t("assessment_dashboard.student.submitted_header_title")}</TH>
            </TR>
          </THead>

          <tbody>
            {pagesWithAssessments.map((page) => {
              // TODO: The selector should allow for non-final submissions so that we can display comments here
              // There should never be more than one assessment for a page and user, but just
              // in case something fishy happens, we handle it gracefully here.
              const officialAssessment = page.assessments[0];
              const points = officialAssessment?.points;
              const submittedDate =
                officialAssessment?.submission &&
                tableDateFormat(officialAssessment?.submission?.created_at);

              const assessmentsWithComments = page.nonOfficialAssessments
                .concat(page.assessments)
                .filter((a) => a.comment?.trim());
              const pageSpecificOutcomes = pageOutcomes.filter((po) => po.page_id === page.id);

              return (
                <TR key={page.id}>
                  <TD className="max-w-52 truncate py-2.5 pl-[17px]" title={page.topic?.title}>
                    {page.topic?.title || "–"}
                  </TD>

                  <TD title={page.title}>
                    <div className="flex items-center gap-2">
                      <div className="min-w-0 max-w-40 truncate">
                        <PageLink
                          className="w-full truncate text-blue-shade-20 underline"
                          page={page}
                        />
                      </div>
                      {assessmentsWithComments.length > 0 &&
                        assessmentsWithComments.map((assessment) => (
                          <Tooltip
                            key={assessment.id}
                            text={
                              <div className="body-xs text-black-tint-40">{assessment.comment}</div>
                            }
                          >
                            <CommentIcon />
                          </Tooltip>
                        ))}
                    </div>
                  </TD>

                  <TD>{getPageType(page, activitiesById)}</TD>

                  <TD>{typeof points === "number" ? `${points}/${page.max_points}` : "-"}</TD>

                  <TD>
                    <Tooltip
                      text={pageSpecificOutcomes
                        .flatMap((po) => {
                          const courseOutcome = courseOutcomes.find(
                            (co) => co.id === po.course_outcome_id,
                          );
                          return courseOutcome ? [displayOutcomeName(courseOutcome.outcome)] : [];
                        })
                        .join(", ")}
                    >
                      {pageSpecificOutcomes.length || "0"}
                    </Tooltip>
                  </TD>

                  <TD>{submittedDate || "-"}</TD>
                </TR>
              );
            })}
          </tbody>
        </Table>
      </ScrollableTableContainer>

      <ScrollableTableContainer title={t("assessment_dashboard.student_outcome_index_title")}>
        <Table>
          {/* Headers: Page / Page Type/ Outcome A / Outcome B / Outcome C */}
          {/* One row per student, showing each student's aggregate outcome scores */}
          <THead>
            <TR>
              <TH className="w-4/12 pl-4">{t("assessment_dashboard.all.page_header_title")}</TH>

              <TH className="w-2/12">
                <Tooltip text={t("assessment_dashboard.all.page_type_header_tooltip")}>
                  {t("assessment_dashboard.all.page_type_header_title")}
                </Tooltip>
              </TH>

              {courseOutcomes.map((courseOutcome) => (
                <TH
                  key={courseOutcome.id}
                  verticalDivClassName="max-h-[180px]"
                  verticalDivTitle={displayOutcomeName(courseOutcome.outcome)}
                  isVertical
                >
                  {displayOutcomeName(courseOutcome.outcome)}
                </TH>
              ))}
            </TR>
          </THead>

          <tbody>
            {pagesWithAssessments.map((page) => (
              <TR key={page.id}>
                <TD className="min-w-0 max-w-24 truncate py-2.5 pl-[17px]">
                  <PageLink className="truncate text-blue-shade-20 underline" page={page} />
                </TD>

                <TD>{getPageType(page, activitiesById)}</TD>

                {courseOutcomes.map((courseOutcome) => {
                  const userOutcomeAssessmentsForThisOutcome = allOutcomeAssessments.filter((oa) =>
                    pageOutcomes.some(
                      (po) =>
                        po.page_id === page.id &&
                        po.course_outcome_id === courseOutcome.id &&
                        oa.page_outcome_id === po.id,
                    ),
                  );
                  const outcomeScore = userOutcomeAssessmentsForThisOutcome.length
                    ? averageAndRound(userOutcomeAssessmentsForThisOutcome.map((oa) => oa.score))
                    : NaN;

                  return (
                    <TD key={courseOutcome.id}>
                      <ScoreIcon value={outcomeScore} />
                    </TD>
                  );
                })}
              </TR>
            ))}
          </tbody>
        </Table>
      </ScrollableTableContainer>
    </main>
  );
};
